import { ref, readonly, type Ref } from 'vue';

const STORAGE_KEY = 'vue_message_drafts_v1';

interface DraftState {
  drafts: Ref<Record<string, string>>;
  loadDraft: (contactId: string | number | null) => string;
  saveDraft: (contactId: string | number | null, text: string) => void;
  deleteDraft: (contactId: string | number | null) => void;
}

const isClient = typeof window !== 'undefined';

function readDrafts(): Record<string, string> {
  if (!isClient) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeDrafts(drafts: Record<string, string>): void {
  if (!isClient) return;
  try {
    const keys = Object.keys(drafts);
    if (keys.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    }
  } catch (e) {
    // ignore storage errors (e.g. private mode, quota exceeded)
  }
}

const drafts = ref<Record<string, string>>(readDrafts());

export function useMessageDrafts(): DraftState {
  return {
    drafts: readonly(drafts) as Ref<Record<string, string>>,
    loadDraft(contactId: string | number | null): string {
      if (!contactId) return '';
      return drafts.value[String(contactId)] || '';
    },
    saveDraft(contactId: string | number | null, text: string): void {
      if (!contactId) return;
      const key = String(contactId);
      const trimmed = text.trim();
      if (trimmed) {
        drafts.value[key] = text;
      } else {
        delete drafts.value[key];
      }
      drafts.value = { ...drafts.value };
      writeDrafts(drafts.value);
    },
    deleteDraft(contactId: string | number | null): void {
      if (!contactId) return;
      const key = String(contactId);
      if (key in drafts.value) {
        delete drafts.value[key];
        drafts.value = { ...drafts.value };
        writeDrafts(drafts.value);
      }
    },
  };
}

export function loadDraft(contactId: string | number | null): string {
  if (!contactId) return '';
  return readDrafts()[String(contactId)] || '';
}

export function saveDraft(contactId: string | number | null, text: string): void {
  if (!contactId) return;
  const draftsMap = readDrafts();
  const key = String(contactId);
  if (text.trim()) {
    draftsMap[key] = text;
  } else {
    delete draftsMap[key];
  }
  writeDrafts(draftsMap);
}

export function deleteDraft(contactId: string | number | null): void {
  if (!contactId) return;
  const draftsMap = readDrafts();
  delete draftsMap[String(contactId)];
  writeDrafts(draftsMap);
}
